import { createEnum } from '../helpers'

// ── DOA types (one configuration page each) ──────────────────────────────────────────────────
// The first four are credit limit review authority and band on the customer's CURRENT CREDIT
// LIMIT. `blocked_orders` is the odd one out: it bands on the CEB (the amount by which a payer is
// over their limit, calculated by SAP and sent on the blocked-orders IDOC), which is why it has its
// own resolver, its own chain builder and its own option keys. See `blocked-orders.ts`.
export const DOA_TYPES = [
  'single_no_deviation',
  'single_with_deviation',
  'mass',
  'initiative',
  'blocked_orders',
] as const
export type DoaType = (typeof DOA_TYPES)[number]

/**
 * The two single-CLR matrices. Like `mass`, they carry no preparer slot: who may prepare a credit
 * limit review — single or mass — is one flat list (`credit_limit_review_preparers`), because
 * preparing is not banded. For a single CLR the chain band is resolved from what the preparer
 * proposes, after the fact; mass has one band whatever the run holds.
 */
export const SINGLE_CLR_DOA_TYPES = ['single_no_deviation', 'single_with_deviation'] as const
export type SingleClrDoaType = (typeof SINGLE_CLR_DOA_TYPES)[number]

export function isSingleClrDoaType(type: DoaType): type is SingleClrDoaType {
  return (SINGLE_CLR_DOA_TYPES as readonly DoaType[]).includes(type)
}

// ── Role slots in the approval chain ─────────────────────────────────────────────────────────
// The four "base" slots are the matrix columns. Each has a delegate counterpart — the person who
// takes over the review when the primary assignee is absent. Delegates are stored as their own
// `doa_role_type` values and follow the exact same enablement rules as their base slot.
export const DOA_BASE_SLOTS = ['preparer', 'reviewer_1', 'reviewer_2', 'approver'] as const
export type DoaBaseSlot = (typeof DOA_BASE_SLOTS)[number]

export const DOA_DELEGATE_SLOTS = [
  'preparer_delegate',
  'reviewer_1_delegate',
  'reviewer_2_delegate',
  'approver_delegate',
] as const
export type DoaDelegateSlot = (typeof DOA_DELEGATE_SLOTS)[number]

// Full set of stored values (base + delegate). Kept under this name so `z.enum(DOA_ROLE_SLOTS)` on
// the backend and the `$type<DoaRoleSlot>()` DB column automatically accept delegates.
export const DOA_ROLE_SLOTS = [
  'preparer',
  'reviewer_1',
  'reviewer_2',
  'approver',
  'preparer_delegate',
  'reviewer_1_delegate',
  'reviewer_2_delegate',
  'approver_delegate',
] as const
export type DoaRoleSlot = (typeof DOA_ROLE_SLOTS)[number]

export const DOA_DELEGATE_SLOT_BY_BASE = {
  preparer: 'preparer_delegate',
  reviewer_1: 'reviewer_1_delegate',
  reviewer_2: 'reviewer_2_delegate',
  approver: 'approver_delegate',
} as const satisfies Record<DoaBaseSlot, DoaDelegateSlot>

export const DOA_BASE_SLOT_BY_DELEGATE = {
  preparer_delegate: 'preparer',
  reviewer_1_delegate: 'reviewer_1',
  reviewer_2_delegate: 'reviewer_2',
  approver_delegate: 'approver',
} as const satisfies Record<DoaDelegateSlot, DoaBaseSlot>

// ── Risk classes ─────────────────────────────────────────────────────────────────────────────
export const DOA_RISK_CLASSES = [
  'all',
  'very_low',
  'low',
  'medium',
  'high',
  'security_deposit',
] as const
export type DoaRiskClass = (typeof DOA_RISK_CLASSES)[number]

// ── How an assignment references its value ───────────────────────────────────────────────────
export const DOA_VALUE_TYPES = ['user', 'role'] as const
export type DoaValueType = (typeof DOA_VALUE_TYPES)[number]

// ── Option keys + their risk class / amount band (shared across types) ───────────────────────
export const DOA_OPTION_KEYS = [
  'all__lte_35k',
  'all__35k_50k',
  'very_low__50k_100k',
  'low__50k_100k',
  'medium__50k_100k',
  'high__50k_100k',
  'security_deposit__50k_100k',
  'all__100k_250k',
  'all__250k_1m',
  'all__1m_1_5m',
  'all__1_5m_5m',
  'all__gt_5m',
  'mass__lt_250k',
  'initiative__all',
  // ── Blocked orders ────────────────────────────────────────────────────────────────────────
  // Deliberately NOT reusing the CLR keys above, even where the bounds are identical. The band
  // hierarchy (`getCoveringDoaOptionKeys`) receives only a key, so a key shared between the two
  // ladders would be undecidable: it could not tell whether to expand within CLR's bands or
  // blocked orders'. The `blocked_orders__` prefix is what makes it decidable — and it is also
  // what keeps the whole set out of `SINGLE_DOA_OPTION_KEYS` with one condition instead of a list
  // someone can forget to extend.
  //
  // `__escalated` twins carry the same bounds as their standard counterpart; escalation is a
  // second axis (see `resolveBlockedOrderDoaOptionKey`), not a different amount.
  'blocked_orders__all__lte_35k',
  'blocked_orders__all__lte_35k__escalated',
  'blocked_orders__very_low__35k_100k',
  'blocked_orders__very_low__35k_100k__escalated',
  'blocked_orders__low__35k_100k',
  'blocked_orders__low__35k_100k__escalated',
  'blocked_orders__medium__35k_50k',
  'blocked_orders__medium__35k_50k__escalated',
  'blocked_orders__high__35k_50k',
  'blocked_orders__high__35k_50k__escalated',
  'blocked_orders__medium__50k_100k',
  'blocked_orders__high__50k_100k',
  'blocked_orders__all__100k_250k',
  'blocked_orders__all__250k_1m',
  'blocked_orders__all__1m_1_5m',
  'blocked_orders__all__1_5m_5m',
  'blocked_orders__all__gt_5m',
] as const
export type DoaOptionKey = (typeof DOA_OPTION_KEYS)[number]

/**
 * Every blocked-orders key starts with this. Both `BLOCKED_ORDER_OPTION_KEYS` and the
 * `SINGLE_DOA_OPTION_KEYS` exclusion derive from it, so adding a key to the list above is enough —
 * nothing else has to be kept in sync.
 */
export const BLOCKED_ORDER_KEY_PREFIX = 'blocked_orders__'

export function isBlockedOrderOptionKey(key: DoaOptionKey): boolean {
  return key.startsWith(BLOCKED_ORDER_KEY_PREFIX)
}

/**
 * The only option mass CLR has. An ordinary key: `DOA_OPTIONS_BY_TYPE.mass` is what keeps it off
 * the single-CLR pages, `SINGLE_DOA_OPTION_KEYS` what keeps `resolveDoaOptionKey` from returning
 * it.
 */
export const MASS_DOA_OPTION_KEY = 'mass__lt_250k' satisfies DoaOptionKey

/**
 * The only option initiatives have. Same containment story as the mass key:
 * `DOA_OPTIONS_BY_TYPE.initiative` keeps it off the other pages, `SINGLE_DOA_OPTION_KEYS` keeps
 * `resolveDoaOptionKey` from returning it — critical here, since an unbounded all-risk band would
 * otherwise match every amount.
 */
export const INITIATIVE_DOA_OPTION_KEY = 'initiative__all' satisfies DoaOptionKey

// `min` is an exclusive lower bound (null = no floor), `max` an upper bound in CAD — inclusive
// unless the band sets `isMaxExclusive` (null = no ceiling). Defined once here so the bands can't
// drift between types.
export const DOA_OPTION_BANDS = {
  all__lte_35k: { riskClass: 'all', min: null, max: 35_000 },
  all__35k_50k: { riskClass: 'all', min: 35_000, max: 50_000 },
  very_low__50k_100k: { riskClass: 'very_low', min: 50_000, max: 100_000 },
  low__50k_100k: { riskClass: 'low', min: 50_000, max: 100_000 },
  medium__50k_100k: { riskClass: 'medium', min: 50_000, max: 100_000 },
  high__50k_100k: { riskClass: 'high', min: 50_000, max: 100_000 },
  security_deposit__50k_100k: { riskClass: 'security_deposit', min: 50_000, max: 100_000 },
  all__100k_250k: { riskClass: 'all', min: 100_000, max: 250_000 },
  all__250k_1m: { riskClass: 'all', min: 250_000, max: 1_000_000 },
  all__1m_1_5m: { riskClass: 'all', min: 1_000_000, max: 1_500_000 },
  all__1_5m_5m: { riskClass: 'all', min: 1_500_000, max: 5_000_000 },
  all__gt_5m: { riskClass: 'all', min: 5_000_000, max: null },
  // The one band bounding the RECOMMENDED limit. The entries above it are the single-CLR bands
  // `resolveDoaOptionKey` matches; mass CLR's boundary is the strategic-account test, which reads
  // the recommendation. See `isMassClrStrategicRecommendation`.
  //
  // Exclusive because that test is `>= 250_000`. The literal repeats
  // `CLR_STRATEGIC_ACCOUNT_THRESHOLD`, which lives in `credit-limit-reviews.ts` and cannot be
  // imported here without a cycle; `massSelectionThreshold.ci.test.ts` pins the two together.
  mass__lt_250k: { riskClass: 'all', min: null, max: 250_000, isMaxExclusive: true },
  // Unbounded on both ends: initiative authority has no dollar threshold. The full-width band is
  // why this key must stay out of `SINGLE_DOA_OPTION_KEYS` — it would match every amount.
  initiative__all: { riskClass: 'all', min: null, max: null },

  // ── Blocked orders: bounds are CEB, not credit limit ──────────────────────────────────────
  // The bottom band has no floor: a CEB at or below zero (the payer is in credit and the block
  // came from something other than the limit check) routes like any other CEB up to $35K. BAT's
  // Order Release DOA file has no row for it.
  blocked_orders__all__lte_35k: { riskClass: 'all', min: null, max: 35_000 },
  blocked_orders__all__lte_35k__escalated: { riskClass: 'all', min: null, max: 35_000 },
  // Very low and low risk get one wide band across 35k–100k; medium and high are split at 50k.
  // That asymmetry is BAT's, from the Order Release DOA file — it is not a mistake.
  blocked_orders__very_low__35k_100k: { riskClass: 'very_low', min: 35_000, max: 100_000 },
  blocked_orders__very_low__35k_100k__escalated: {
    riskClass: 'very_low',
    min: 35_000,
    max: 100_000,
  },
  blocked_orders__low__35k_100k: { riskClass: 'low', min: 35_000, max: 100_000 },
  blocked_orders__low__35k_100k__escalated: { riskClass: 'low', min: 35_000, max: 100_000 },
  blocked_orders__medium__35k_50k: { riskClass: 'medium', min: 35_000, max: 50_000 },
  blocked_orders__medium__35k_50k__escalated: { riskClass: 'medium', min: 35_000, max: 50_000 },
  blocked_orders__high__35k_50k: { riskClass: 'high', min: 35_000, max: 50_000 },
  blocked_orders__high__35k_50k__escalated: { riskClass: 'high', min: 35_000, max: 50_000 },
  // No escalated twins: the file marks the flag columns "NA" for these two rows.
  blocked_orders__medium__50k_100k: { riskClass: 'medium', min: 50_000, max: 100_000 },
  blocked_orders__high__50k_100k: { riskClass: 'high', min: 50_000, max: 100_000 },
  // Above 100k the file leaves the flag columns empty, so there is no escalation axis here yet.
  blocked_orders__all__100k_250k: { riskClass: 'all', min: 100_000, max: 250_000 },
  blocked_orders__all__250k_1m: { riskClass: 'all', min: 250_000, max: 1_000_000 },
  blocked_orders__all__1m_1_5m: { riskClass: 'all', min: 1_000_000, max: 1_500_000 },
  blocked_orders__all__1_5m_5m: { riskClass: 'all', min: 1_500_000, max: 5_000_000 },
  blocked_orders__all__gt_5m: { riskClass: 'all', min: 5_000_000, max: null },
} as const satisfies Record<
  DoaOptionKey,
  {
    riskClass: DoaRiskClass
    min: number | null
    max: number | null
    isMaxExclusive?: boolean
  }
>

/**
 * The bands a single CLR can resolve to: every key except the mass row, the initiative row and the
 * whole blocked-orders ladder. `resolveDoaOptionKey` and the hierarchy expanders iterate this
 * rather than `DOA_OPTION_KEYS`, making those keys unreachable from an amount and inert in the
 * covering/covered hierarchy.
 *
 * The blocked-orders exclusion is load-bearing, not tidiness. Several of those bands overlap CLR
 * bands by construction — `blocked_orders__all__lte_35k__escalated` has the same bounds as
 * `all__lte_35k`, so it would capture every customer with a null credit limit. A leak here reroutes real credit limit reviews to the wrong people with no
 * error anywhere near the cause; `blockedOrdersDoaShape.ci.test.ts` pins the result.
 */
export const SINGLE_DOA_OPTION_KEYS = DOA_OPTION_KEYS.filter(
  (key) =>
    key !== MASS_DOA_OPTION_KEY &&
    key !== INITIATIVE_DOA_OPTION_KEY &&
    !isBlockedOrderOptionKey(key),
) as readonly DoaOptionKey[]

/** The blocked-orders ladder: the keys its own hierarchy expands over. */
export const BLOCKED_ORDER_OPTION_KEYS = DOA_OPTION_KEYS.filter((key) =>
  isBlockedOrderOptionKey(key),
) as readonly DoaOptionKey[]

export type DoaOption = {
  riskClass: DoaRiskClass
  min: number | null
  max: number | null
  /** `max` is a strict bound (`< max`) rather than the default inclusive `<= max`. */
  isMaxExclusive?: boolean
  /** Whether the chain starts with a preparer slot; off for every CLR type, whose preparers are
   *  the flat list, and on for initiatives only. */
  hasPreparer: boolean
  requiresReviewer1: boolean
  requiresReviewer2: boolean
  requiresAllToApprove: boolean // approver needs ALL assignees to approve
}

/**
 * A blocked-orders option. The CLR shape hardcodes the approver as always present —
 * `isDoaSlotEnabled` returns true for it unconditionally, and `getClrChainSlots` ends in a bare
 * `return true`. Blocked orders needs no such guarantee: the approver is a per-band flag rather
 * than a given — every band currently sets it, but the shape does not assume it.
 *
 * Rather than loosen the CLR shape, blocked orders carries the extra flag and is read through its
 * own `isBlockedOrderSlotEnabled` / `getBlockedOrderChainSlots` in `blocked-orders.ts`.
 */
export type BlockedOrderDoaOption = DoaOption & { requiresApprover: boolean }

type DoaOptionFlags = Partial<
  Pick<
    DoaOption,
    'hasPreparer' | 'requiresReviewer1' | 'requiresReviewer2' | 'requiresAllToApprove'
  >
>

// Defaults for the common case: no preparer slot (every CLR type — single and mass — reads its
// preparers from the flat `credit_limit_review_preparers` list, see `SINGLE_CLR_DOA_TYPES`),
// Reviewer 1 required, Reviewer 2 not, single approver. Each row in the matrix only overrides what
// differs; initiatives are the one type that turns the preparer slot back on.
const DEFAULT_FLAGS = {
  hasPreparer: false,
  requiresReviewer1: true,
  requiresReviewer2: false,
  requiresAllToApprove: false,
} as const

// Helper to build an option from its shared band + the defaults + per-type flag overrides.
function band(key: DoaOptionKey, flags: DoaOptionFlags = {}): DoaOption {
  return { ...DOA_OPTION_BANDS[key], ...DEFAULT_FLAGS, ...flags }
}

// Blocked-orders defaults, which invert three of the CLR ones: no preparer (the system creates the
// record), no reviewer (the file shows N/A below $250K) and never a Reviewer 2. The approver is the
// single actor on most bands.
const BLOCKED_ORDER_DEFAULT_FLAGS = {
  hasPreparer: false,
  requiresReviewer1: false,
  requiresReviewer2: false,
  requiresAllToApprove: false,
  requiresApprover: true,
} as const

type BlockedOrderDoaOptionFlags = Partial<
  Pick<
    BlockedOrderDoaOption,
    'requiresReviewer1' | 'requiresReviewer2' | 'requiresAllToApprove' | 'requiresApprover'
  >
>

function blockedOrderBand(
  key: DoaOptionKey,
  flags: BlockedOrderDoaOptionFlags = {},
): BlockedOrderDoaOption {
  return { ...DOA_OPTION_BANDS[key], ...BLOCKED_ORDER_DEFAULT_FLAGS, ...flags }
}

/**
 * A mapped type rather than `Record<DoaType, Partial<Record<DoaOptionKey, DoaOption>>>`, because
 * blocked-orders options carry `requiresApprover` and excess-property checking applies under
 * `satisfies` — the plain Record would reject them.
 */
type DoaOptionsByType = {
  [T in DoaType]: Partial<
    Record<DoaOptionKey, T extends 'blocked_orders' ? BlockedOrderDoaOption : DoaOption>
  >
}

/**
 * The matrix. A key present under a type = that option applies to the page; absent = not
 * applicable, which is how `mass__lt_250k` stays off the single-CLR pages and the twelve bands off
 * the mass one. Reviewer cells render grey when not required.
 */
export const DOA_OPTIONS_BY_TYPE = {
  single_no_deviation: {
    all__lte_35k: band('all__lte_35k', { requiresReviewer1: false }),
    all__35k_50k: band('all__35k_50k', { requiresReviewer1: false }),
    very_low__50k_100k: band('very_low__50k_100k', { requiresReviewer1: false }),
    low__50k_100k: band('low__50k_100k', { requiresReviewer1: false }),
    medium__50k_100k: band('medium__50k_100k', { requiresReviewer1: false }),
    high__50k_100k: band('high__50k_100k', { requiresReviewer1: false }),
    security_deposit__50k_100k: band('security_deposit__50k_100k', {
      requiresReviewer1: false,
    }),
    all__100k_250k: band('all__100k_250k', { requiresReviewer1: false }),
    all__250k_1m: band('all__250k_1m'),
    all__1m_1_5m: band('all__1m_1_5m', { requiresReviewer2: true }),
    all__1_5m_5m: band('all__1_5m_5m', { requiresReviewer2: true }),
    all__gt_5m: band('all__gt_5m', { requiresReviewer2: true, requiresAllToApprove: true }),
  },
  single_with_deviation: {
    all__lte_35k: band('all__lte_35k'),
    all__35k_50k: band('all__35k_50k'),
    very_low__50k_100k: band('very_low__50k_100k'),
    low__50k_100k: band('low__50k_100k'),
    medium__50k_100k: band('medium__50k_100k'),
    high__50k_100k: band('high__50k_100k'),
    security_deposit__50k_100k: band('security_deposit__50k_100k'),
    all__100k_250k: band('all__100k_250k'),
    all__250k_1m: band('all__250k_1m'),
    all__1m_1_5m: band('all__1m_1_5m', { requiresReviewer2: true }),
    all__1_5m_5m: band('all__1_5m_5m', { requiresReviewer2: true }),
    all__gt_5m: band('all__gt_5m', { requiresReviewer2: true, requiresAllToApprove: true }),
  },
  // One row, one chain, whatever the run holds: the Approver alone. No preparer slot — who may
  // start a run is the same flat preparer list single CLR reads (`SINGLE_CLR_DOA_TYPES`). No
  // reviewer at all since the phase 2 UAT (Oct 2026) — the approver does the line-by-line pass
  // (comments, checks, exclusions) and sends back to the preparer. ONE approver signature resolves
  // the run: it was unanimous until
  // CT-9 UAT, when "every primary approver" expanded to every account holding the approver role
  // and a run stalled until all of them signed. The unanimity machinery is flag-driven and stays
  // intact (single CLR >$5M and initiatives still use it), so this is the one line that decides
  // it — see `massApprovalQuorum.ci.test.ts` and `massDoaShape.ci.test.ts` before changing it.
  mass: {
    mass__lt_250k: band('mass__lt_250k', { requiresReviewer1: false }),
  },
  // One row covering every initiative, whatever its size: no threshold, all risk classes, and
  // every configured primary approver must sign (FUZE-635). No Reviewer 2 — the initiative flow
  // has a single review step (`Draft -> In review -> Awaiting approval`), so a second reviewer
  // slot would be a stage no configured user could ever act on. The one type that still assigns
  // its preparers here: initiatives never joined the CLR preparer list.
  initiative: {
    initiative__all: band('initiative__all', {
      hasPreparer: true,
      requiresReviewer1: true,
      requiresAllToApprove: true,
    }),
  },
  // Bands on the CEB, not the credit limit. Reviewer is absent below $250K — the file shows the
  // Reviewer column as N/A there, so the approver is the single actor.
  blocked_orders: {
    blocked_orders__all__lte_35k: blockedOrderBand('blocked_orders__all__lte_35k'),
    blocked_orders__all__lte_35k__escalated: blockedOrderBand(
      'blocked_orders__all__lte_35k__escalated',
    ),
    blocked_orders__very_low__35k_100k: blockedOrderBand('blocked_orders__very_low__35k_100k'),
    blocked_orders__very_low__35k_100k__escalated: blockedOrderBand(
      'blocked_orders__very_low__35k_100k__escalated',
    ),
    blocked_orders__low__35k_100k: blockedOrderBand('blocked_orders__low__35k_100k'),
    blocked_orders__low__35k_100k__escalated: blockedOrderBand(
      'blocked_orders__low__35k_100k__escalated',
    ),
    blocked_orders__medium__35k_50k: blockedOrderBand('blocked_orders__medium__35k_50k'),
    blocked_orders__medium__35k_50k__escalated: blockedOrderBand(
      'blocked_orders__medium__35k_50k__escalated',
    ),
    blocked_orders__high__35k_50k: blockedOrderBand('blocked_orders__high__35k_50k'),
    blocked_orders__high__35k_50k__escalated: blockedOrderBand(
      'blocked_orders__high__35k_50k__escalated',
    ),
    blocked_orders__medium__50k_100k: blockedOrderBand('blocked_orders__medium__50k_100k'),
    blocked_orders__high__50k_100k: blockedOrderBand('blocked_orders__high__50k_100k'),
    blocked_orders__all__100k_250k: blockedOrderBand('blocked_orders__all__100k_250k'),
    // From $250K up the file names a reviewer as well as an approver.
    blocked_orders__all__250k_1m: blockedOrderBand('blocked_orders__all__250k_1m', {
      requiresReviewer1: true,
    }),
    blocked_orders__all__1m_1_5m: blockedOrderBand('blocked_orders__all__1m_1_5m', {
      requiresReviewer1: true,
    }),
    blocked_orders__all__1_5m_5m: blockedOrderBand('blocked_orders__all__1_5m_5m', {
      requiresReviewer1: true,
    }),
    // Six named approvers in the file — the credit committee. Every primary must sign.
    blocked_orders__all__gt_5m: blockedOrderBand('blocked_orders__all__gt_5m', {
      requiresReviewer1: true,
      requiresAllToApprove: true,
    }),
  },
} as const satisfies DoaOptionsByType

// ── Resolvers (single source for backend validation + frontend rendering) ─────────────────────

/** Display order of the options within a page. `getDoaOptions` drops the ones a type lacks. */
export const DOA_OPTION_ORDER: readonly DoaOptionKey[] = DOA_OPTION_KEYS

export function getDoaOption(type: DoaType, key: DoaOptionKey): DoaOption | undefined {
  return (DOA_OPTIONS_BY_TYPE[type] as Partial<Record<DoaOptionKey, DoaOption>>)[key]
}

export function getDoaOptions(type: DoaType): { key: DoaOptionKey; option: DoaOption }[] {
  return DOA_OPTION_ORDER.flatMap((key) => {
    const option = getDoaOption(type, key)
    return option ? [{ key, option }] : []
  })
}

/** The matrix columns for a type: the base slots at least one of its options enables. */
export function getDoaBaseSlotsForType(type: DoaType): DoaBaseSlot[] {
  return DOA_BASE_SLOTS.filter((slot) =>
    getDoaOptions(type).some(({ option }) => isDoaSlotEnabled(option, slot)),
  )
}

/** Normalizes a delegate slot to its base slot; base slots pass through unchanged. */
export function toDoaBaseSlot(slot: DoaRoleSlot): DoaBaseSlot {
  return (
    (DOA_BASE_SLOT_BY_DELEGATE as Record<string, DoaBaseSlot | undefined>)[slot] ??
    (slot as DoaBaseSlot)
  )
}

export function isDoaSlotEnabled(option: DoaOption, slot: DoaRoleSlot): boolean {
  // A delegate is enabled exactly when its base slot is — the rules are identical.
  switch (toDoaBaseSlot(slot)) {
    case 'reviewer_1':
      return option.requiresReviewer1
    case 'reviewer_2':
      return option.requiresReviewer2
    case 'preparer':
      return option.hasPreparer
    case 'approver':
      return true
  }
}

export function isDoaAllApprovalRequired(option: DoaOption): boolean {
  return option.requiresAllToApprove
}

// ── Primary / delegate integrity ──────────────────────────────────────────────────────────────

// Business-rule error keys for the save endpoint. Emitted as a ZodIssue `params.type` so the
// frontend can translate them (the value matches the i18n key under `delegationOfAuthority`).
export const DOA_SAVE_ERRORS = ['delegateSameAsPrimary'] as const
export const DOA_SAVE_ERROR_TYPES = createEnum(DOA_SAVE_ERRORS)
export type DoaSaveErrorType = (typeof DOA_SAVE_ERRORS)[number]

/** The minimal shape needed to check assignments for primary/delegate conflicts. */
export type DoaAssignmentLike = {
  riskThresholdOptionKey: DoaOptionKey
  doaRoleType: DoaRoleSlot
  valueType: DoaValueType
  valueId: string
}

export type DoaPrimaryDelegateConflict = {
  riskThresholdOptionKey: DoaOptionKey
  baseSlot: DoaBaseSlot
  valueType: DoaValueType
  valueId: string
}

/**
 * A delegate must not be the same entity (same role or same user) as the primary of its own slot
 * for the same risk-threshold option. Returns every such conflict in the given set of assignments.
 * The check is per slot family — being a Preparer primary and an Approver delegate is fine.
 */
export function findDoaPrimaryDelegateConflicts(
  assignments: DoaAssignmentLike[],
): DoaPrimaryDelegateConflict[] {
  // Bucket each (option, base slot) into the primary values and delegate values it holds.
  const buckets = new Map<
    string,
    {
      riskThresholdOptionKey: DoaOptionKey
      baseSlot: DoaBaseSlot
      primary: Set<string>
      delegate: Set<string>
    }
  >()

  for (const assignment of assignments) {
    const baseSlot = toDoaBaseSlot(assignment.doaRoleType)
    const bucketKey = `${assignment.riskThresholdOptionKey}::${baseSlot}`
    const bucket = buckets.get(bucketKey) ?? {
      riskThresholdOptionKey: assignment.riskThresholdOptionKey,
      baseSlot,
      primary: new Set<string>(),
      delegate: new Set<string>(),
    }
    const value = `${assignment.valueType}:${assignment.valueId}`
    if (assignment.doaRoleType === baseSlot) bucket.primary.add(value)
    else bucket.delegate.add(value)
    buckets.set(bucketKey, bucket)
  }

  const conflicts: DoaPrimaryDelegateConflict[] = []
  for (const bucket of buckets.values()) {
    for (const value of bucket.delegate) {
      if (!bucket.primary.has(value)) continue
      const idx = value.indexOf(':')
      conflicts.push({
        riskThresholdOptionKey: bucket.riskThresholdOptionKey,
        baseSlot: bucket.baseSlot,
        valueType: value.slice(0, idx) as DoaValueType,
        valueId: value.slice(idx + 1),
      })
    }
  }
  return conflicts
}
